/**
 * Screener API
 *
 * Multi-instrument screener API using RTK Query.
 * Supports funds and benchmarks, with extensibility for additional instruments.
 *
 * Architecture:
 * - Each instrument type implements the InstrumentConfig interface
 * - All instruments are defined in the INSTRUMENTS array
 * - Endpoints are built dynamically from the array
 * - State and cache are maintained separately per instrument type
 */

import { createApi } from '@reduxjs/toolkit/query/react';
import { createBaseQuery } from '../base';
import { fundsInstrumentConfig } from './instruments/funds';
import { benchmarksInstrumentConfig } from './instruments/benchmarks';
import { capitalizeFirst } from '@/utils/strings';
import { getTagForInstrument } from './utils';

const INSTRUMENTS = [fundsInstrumentConfig, benchmarksInstrumentConfig] as const;

export const screenerApi = createApi({
  reducerPath: 'screenerApi',
  baseQuery: createBaseQuery(),
  tagTypes: INSTRUMENTS.map(config => getTagForInstrument(config.type)),
  endpoints: builder => ({
    ...Object.fromEntries(
      Object.entries(fundsInstrumentConfig.buildEndpoints(builder)).map(([key, value]) => [`${key}${capitalizeFirst(fundsInstrumentConfig.type)}`, value]),
    ),
    ...Object.fromEntries(
      Object.entries(benchmarksInstrumentConfig.buildEndpoints(builder)).map(([key, value]) => [
        `${key}${capitalizeFirst(benchmarksInstrumentConfig.type)}`,
        value,
      ]),
    ),
  }),
});

export const {
  // Funds hooks
  useGetFiltersFundsQuery,
  useLazyGetFiltersFundsQuery,
  useSearchFundsQuery,
  useLazySearchFundsQuery,

  // Benchmarks hooks
  useGetFiltersBenchmarksQuery,
  useLazyGetFiltersBenchmarksQuery,
  useSearchBenchmarksQuery,
  useLazySearchBenchmarksQuery,
} = screenerApi;
